import { useEffect, useRef, useState } from "react";
import Lenis from "lenis";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import Nav from "@/components/mdh/Nav";
import Hero from "@/components/mdh/Hero";
import Marquee from "@/components/mdh/Marquee";
import Manifesto from "@/components/mdh/Manifesto";
import CommandCenter from "@/components/mdh/CommandCenter";
import Capabilities from "@/components/mdh/Capabilities";
import DeveloperWorkflow from "@/components/mdh/DeveloperWorkflow";
import FAQ from "@/components/mdh/FAQ";
import Footer from "@/components/mdh/Footer";
import Docs from "@/pages/Docs";
import Admin from "@/pages/Admin";
import Changelog from "@/pages/Changelog";
import Auth from "@/pages/Auth";
import Reset from "@/pages/Reset";
import Onboarding from "@/pages/Onboarding";
import Dashboard from "@/pages/Dashboard";
import Templates from "@/pages/Templates";
import Settings from "@/pages/Settings";
import Security from "@/pages/Security";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import Portal from "@/pages/Portal";
import AuthCallback from "@/components/AuthCallback";
import AssistantWidget from "@/components/mdh/AssistantWidget";
import CommandPalette from "@/components/mdh/CommandPalette";
import { setLenis } from "@/components/mdh/primitives";
import { MotionProvider, useMotion } from "@/components/mdh/motion";
import { AuthProvider, useAuth } from "@/auth/AuthContext";

const LoadingScreen = () => (
    <div className="flex min-h-screen items-center justify-center">
        <p className="font-mono text-xs text-slate-500">Loading…</p>
    </div>
);

const ProtectedRoute = ({ children }) => {
    const { user, loading } = useAuth();
    const location = useLocation();
    if (location.state?.user) return children;
    if (loading) return <LoadingScreen />;
    if (!user) return <Navigate to="/login" replace />;
    return children;
};

const Home = () => (
    <>
        <Nav />
        <main>
            <Hero />
            <Marquee />
            <Manifesto />
            <CommandCenter />
            <Capabilities />
            <DeveloperWorkflow />
            <FAQ />
        </main>
        <Footer />
    </>
);

function Shell() {
    const [paletteOpen, setPaletteOpen] = useState(false);
    const { reduced } = useMotion();
    const lenisRef = useRef(null);
    const location = useLocation();

    useEffect(() => {
        const lenis = new Lenis({ lerp: 0.12, smoothWheel: true, wheelMultiplier: 1 });
        lenisRef.current = lenis;
        setLenis(lenis);
        let rafId;
        const raf = (time) => {
            lenis.raf(time);
            rafId = requestAnimationFrame(raf);
        };
        rafId = requestAnimationFrame(raf);
        return () => {
            cancelAnimationFrame(rafId);
            setLenis(null);
            lenisRef.current = null;
            lenis.destroy();
        };
    }, []);

    useEffect(() => {
        const lenis = lenisRef.current;
        if (!lenis) return;
        if (reduced) lenis.stop();
        else lenis.start();
    }, [reduced]);

    useEffect(() => {
        lenisRef.current?.scrollTo(0, { immediate: true });
        window.scrollTo(0, 0);
    }, [location.pathname]);

    useEffect(() => {
        const onKey = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
                e.preventDefault();
                setPaletteOpen((v) => !v);
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, []);

    // Detect the OAuth session_id synchronously during render (not in an
    // effect) so the exchange runs before any protected-route auth check.
    const isCallback = location.hash?.includes("session_id=");

    return (
        <div
            className={`relative min-h-screen overflow-x-clip bg-[#0B0F17] text-slate-200 ${
                reduced ? "motion-off" : ""
            }`}
        >
            <div className="noise-overlay" aria-hidden="true" />
            {isCallback ? (
                <AuthCallback />
            ) : (
                <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/docs" element={<Docs />} />
                    <Route path="/changelog" element={<Changelog />} />
                    <Route path="/admin" element={<Admin />} />
                    <Route path="/login" element={<Auth key="login" mode="login" />} />
                    <Route path="/signup" element={<Auth key="signup" mode="signup" />} />
                    <Route path="/reset" element={<Reset />} />
                    <Route
                        path="/onboarding"
                        element={
                            <ProtectedRoute>
                                <Onboarding />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/dashboard"
                        element={
                            <ProtectedRoute>
                                <Dashboard />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/templates"
                        element={
                            <ProtectedRoute>
                                <Templates />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/settings"
                        element={
                            <ProtectedRoute>
                                <Settings />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/security"
                        element={
                            <ProtectedRoute>
                                <Security />
                            </ProtectedRoute>
                        }
                    />
                    <Route path="/portal/:slug" element={<Portal />} />
                    <Route path="/terms" element={<Terms />} />
                    <Route path="/privacy" element={<Privacy />} />
                </Routes>
            )}
            <AssistantWidget />
            <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
            <Toaster theme="dark" position="bottom-right" />
        </div>
    );
}

function App() {
    return (
        <MotionProvider>
            <BrowserRouter>
                <AuthProvider>
                    <Shell />
                </AuthProvider>
            </BrowserRouter>
        </MotionProvider>
    );
}

export default App;
